import { useQueries, useQuery } from "@tanstack/react-query";

import { axiosInstance } from "../../lib/axios";
import { INTEGRATION_SCOPES } from "../constants";

import type { IntegrationScope } from "../constants";
import type { GetCrawlerPreferenceResponse } from "../types/crawlerPreference";

type ReadableScope = Extract<IntegrationScope, "user" | "deployment">;

export const CRAWLER_PREFERENCE_QUERY_KEY = (
  source: string,
  verifiableId: string,
  integrationScope: ReadableScope = INTEGRATION_SCOPES.USER,
) => ["crawler-preference", source, verifiableId, integrationScope] as const;

const crawlerPreferenceQueryOptions = (
  source: string,
  verifiableId: string,
  integrationScope: ReadableScope = INTEGRATION_SCOPES.USER,
) => ({
  queryKey: CRAWLER_PREFERENCE_QUERY_KEY(
    source,
    verifiableId,
    integrationScope,
  ),
  queryFn: async () =>
    axiosInstance.get<GetCrawlerPreferenceResponse>("/preference/crawler", {
      params: { source, verifiableId, integration_scope: integrationScope },
    }),
  select: (response: { data: GetCrawlerPreferenceResponse }) =>
    response.data.results,
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
  // Poll while the crawler is still building the tree (WAIT).
  refetchInterval: (query: {
    state: { data?: { data?: GetCrawlerPreferenceResponse } };
  }) => {
    const results = query.state.data?.data?.results;
    return results && "process" in results && results.process === "WAIT"
      ? 2000
      : false;
  },
  refetchIntervalInBackground: false,
});

export const useFetchCrawlerPreference = (
  source: string,
  verifiableId: string,
  options?: { enabled?: boolean; integrationScope?: ReadableScope },
) =>
  useQuery({
    ...crawlerPreferenceQueryOptions(
      source,
      verifiableId,
      options?.integrationScope,
    ),
    enabled: (options?.enabled ?? true) && !!source && !!verifiableId,
  });

export const useFetchCrawlerPreferencesForAccounts = (
  accounts: { source: string; verifiableId: string }[],
  integrationScope?: ReadableScope,
) =>
  useQueries({
    queries: accounts.map((account) => ({
      ...crawlerPreferenceQueryOptions(
        account.source,
        account.verifiableId,
        integrationScope,
      ),
      // Guard a half-formed pair, like the single-account hook does.
      enabled: !!account.source && !!account.verifiableId,
    })),
  });
